<template>
  <div class="auth-wrapper auth-v2">
    <div class="auth-inner">
      <!-- brand logo -->
      <v-avatar
        size="30px"
        color="#f4f5fa"
        class="brand-logo d-flex align-center me-3"
      >
        <v-img
          :src="appLogo"
          alt="logo"
          contain
          eager
        ></v-img>
      </v-avatar>
      <!--/ brand logo -->

      <v-row class="auth-row ma-0">
        <v-col
          lg="8"
          class="d-none d-lg-block position-relative overflow-hidden pa-0"
        >
          <div class="auth-illustrator-wrapper">
            <!-- triangle bg -->
            <!-- <img
              height="362"
              class="auth-mask-bg"
              :src="require(`@/assets/images/misc/mask-v2-${$vuetify.theme.dark ? 'dark' : 'light'}.png`)"
            /> -->

            <!-- 3d character -->
            <div class="d-flex align-center h-full pa-16 pe-0">
              <v-img
                contain
                max-width="100%"
                height="510"
                class="auth-3d-group"
                :src="require(`@/assets/images/logos/TECH_Horizontal_${$vuetify.theme.dark ? 'White' : 'Color'}.png`)"
              ></v-img>
            </div>
          </div>
        </v-col>

        <v-col
          lg="4"
          class="d-flex align-center auth-bg pt-0 pb-0"
        >
          <v-row>
            <v-col
              cols="12"
              sm="8"
              md="6"
              lg="12"
              class="mx-auto"
            >
              <v-card flat>
                <!-- login form -->
                <v-card-text>
                  <v-form
                    ref="registerForm"
                    @submit.prevent="handleFormSubmit"
                  >
                    <v-text-field
                      v-model="nome"
                      outlined
                      label="Nome da empresa"
                      :error-messages="errorMessages.nome"
                      :rules="[validators.requiredSemMensagem]"
                      placeholder="Empresa"
                      hide-details="auto"
                      class="mb-4"
                    ></v-text-field>

                    <v-text-field
                      v-model="sobrenome"
                      outlined
                      label="Nome do responsável"
                      placeholder="Responsável"
                      hide-details="auto"
                      class="mb-4"
                      :rules="[validators.requiredSemMensagem]"
                    ></v-text-field>

                    <v-text-field
                      v-model="email"
                      outlined
                      :error-messages="errorMessages.email"
                      :rules="[validators.requiredSemMensagem, validators.emailValidator]"
                      label="Email corporativo"
                      placeholder="Email corporativo"
                      hide-details="auto"
                      class="mb-4"
                    ></v-text-field>

                    <v-text-field
                      v-model="senha"
                      outlined
                      :type="isPasswordVisible ? 'text' : 'password'"
                      label="Senha"
                      :error-messages="errorMessages.password"
                      placeholder="Senha"
                      :rules="[validators.requiredSemMensagem, validators.passwordValidator]"
                      :append-icon="isPasswordVisible ? icons.mdiEyeOffOutline : icons.mdiEyeOutline"
                      hide-details="auto"
                      class="mb-4"
                      @click:append="isPasswordVisible = !isPasswordVisible"
                    ></v-text-field>

                    <v-text-field
                      v-model="telefone"
                      v-mask="'(##) #####-####'"
                      outlined
                      label="Telefone celular"
                      placeholder="Telefone celular"
                      hide-details="auto"
                      class="mb-4"
                      :rules="[validators.requiredSemMensagem, validators.isTelefoneCelularValido]"
                    ></v-text-field>

                    <v-checkbox
                      hide-details
                      class="mt-0"
                    >
                      <template #label>
                        <div class="d-flex align-center flex-wrap text-subtitle-1">
                          <span class="me-2">Eu concordo com a </span><a
                            href="javascript:void(0)"
                          >política de privacidade</a>
                        </div>
                      </template>
                    </v-checkbox>

                    <v-btn
                      block
                      color="primary"
                      type="submit"
                      class="mt-4"
                    >
                      <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="secundary"
                      ></v-progress-circular>
                      <span v-else>Inscreva-se</span>
                    </v-btn>
                  </v-form>
                </v-card-text>

                <!-- create new account  -->
                <v-card-text class="d-flex align-center justify-center flex-wrap mt-2">
                  <p class="mb-0 me-2">
                    Já tem uma conta?
                  </p>
                  <router-link :to="{ name: 'auth-login' }">
                    Em vez disso faça login
                  </router-link>
                </v-card-text>

                <!-- divider -->
                <v-card-text class="d-flex align-center mt-2">
                  <v-divider></v-divider>
                  <span class="mx-5">ou</span>
                  <v-divider></v-divider>
                </v-card-text>

                <!-- social links -->
                <v-card-actions class="d-flex justify-center">
                  <v-btn
                    v-for="link in socialLink"
                    :key="link.icon"
                    icon
                    class="ms-1"
                  >
                    <v-icon :color="$vuetify.theme.dark ? link.colorInDark : link.color">
                      {{ link.icon }}
                    </v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { ref } from '@vue/composition-api'
import {
  mdiEyeOffOutline, mdiEyeOutline, mdiFacebook, mdiGoogle,
} from '@mdi/js'
import { useRouter } from '@core/utils'
import {
  alphaValidator, emailValidator, passwordValidator, requiredSemMensagem,
} from '@core/utils/validation'
import themeConfig from '@themeConfig'
import jwtDecode from 'jwt-decode'
import { isTelefoneCelularValido } from '@/utils/validation'
import DocumentoService from '@/services/modules/DocumentoService'
import useAuthStore from '@/store/auth'
import useUserStore from '@/store/user'
import FuncionalidadeService from '@/services/modules/FuncionalidadeService'
import EmpresaService from '@/services/modules/EmpresaService'
import AuthService from '@/services/modules/AuthService'

export default {
  setup() {
    const { setFuncionalidades, setAccessToken } = useAuthStore()

    const userStore = useUserStore()
    const { setUserData } = userStore

    const registerForm = ref(null)
    const { router } = useRouter()
    const isPasswordVisible = ref(false)
    const nome = ref('')
    const email = ref('')
    const sobrenome = ref('')
    const senha = ref('')
    const telefone = ref('')
    const errorMessages = ref([])
    const loading = ref(false)
    const socialLink = [
      {
        icon: mdiFacebook,
        color: '#4267b2',
        colorInDark: '#4267b2',
      },
      {
        icon: mdiGoogle,
        color: '#db4437',
        colorInDark: '#db4437',
      },
    ]

    const getFuncionalidades = async id => {
      const funcionalidades = []

      const funcionalidadesIds = await FuncionalidadeService.getFuncionalidadesByColaborador()

      funcionalidades.value = funcionalidadesIds
      funcionalidades.push(1, 2, 3)

      return funcionalidades
    }

    const handleFormSubmit = async () => {
      const isFormValid = registerForm.value.validate()

      if (!isFormValid) return

      try {
        loading.value = true

        await EmpresaService.criar({
          nome: nome.value,
          email: email.value,
          sobrenome: sobrenome.value,
          senha: senha.value,
          telefone: telefone.value,
        })

        const accessToken = await AuthService.login(email.value, senha.value)

        setAccessToken(accessToken)

        const decoded = jwtDecode(accessToken)

        const avatar = await DocumentoService.getFotoPerfil(decoded.nameid)
        const user = {
          id: decoded.nameid,
          ide: decoded.ide,
          nome: decoded.unique_name,
          avatar,
          cadastroCompleto: decoded.c,
        }
        setUserData(user)

        const funcionalidadeIds = await getFuncionalidades(1000)
        setFuncionalidades(funcionalidadeIds)

        loading.value = false
        router.push('/')
      } catch (error) {
        loading.value = false
      }
    }

    return {
      isPasswordVisible,
      nome,
      sobrenome,
      email,
      senha,
      telefone,
      errorMessages,
      handleFormSubmit,
      socialLink,
      loading,
      icons: {
        mdiEyeOutline,
        mdiEyeOffOutline,
      },
      validators: {
        requiredSemMensagem,
        emailValidator,
        passwordValidator,
        alphaValidator,
        isTelefoneCelularValido,
      },

      // themeConfig
      appName: themeConfig.app.name,
      appLogo: themeConfig.app.logo,

      // Template Refs
      registerForm,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@core/preset/preset/pages/auth.scss';

.maxh-100 {
  max-height: 100%;
}

.alert {
  height: 50px;
}

.border-green {
  border: 2px solid green;
}
</style>
